import React, { useEffect, useState, useMemo } from 'react';
import { FolderOpen, Search, Trash2, Upload, Loader2, Film, FileText, ImageOff, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

/**
 * Pagina admin "Archivio immagini" (Media Library standalone).
 * Mostra tutti i file in /api/uploads/ con ricerca, upload, elimina, copia URL.
 */
export default function MediaLibraryPage() {
  const [files, setFiles] = useState(null);
  const [q, setQ] = useState('');
  const [folder, setFolder] = useState('all');
  const [busy, setBusy] = useState(false);

  const reload = () => api.listMedia().then(setFiles).catch(() => setFiles([]));
  useEffect(() => { reload(); }, []);

  const folders = useMemo(() => {
    const s = new Set();
    (files || []).forEach((f) => s.add(f.folder || 'root'));
    return ['all', ...Array.from(s).sort()];
  }, [files]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let list = files || [];
    if (folder !== 'all') list = list.filter((f) => (f.folder || 'root') === folder);
    if (ql) list = list.filter((f) => f.name.toLowerCase().includes(ql) || (f.folder || '').toLowerCase().includes(ql));
    return list;
  }, [files, q, folder]);

  const handleFiles = async (list) => {
    setBusy(true);
    for (const f of Array.from(list)) {
      try { await api.uploadFile(f, 'library'); }
      catch (e) { toast.error(`Errore ${f.name}: ${e?.message}`); }
    }
    setBusy(false);
    toast.success('Upload completato');
    reload();
  };

  const del = async (f) => {
    if (!window.confirm(`Eliminare "${f.name}"?`)) return;
    try { await api.deleteMedia(f.rel); reload(); toast.success('File eliminato'); }
    catch (e) { toast.error(e?.message); }
  };

  const copy = (f) => {
    // f.url è path relativo (es. /api/uploads/...). Per copiare URL esterno,
    // anteponi l'origin.
    const url = `${window.location.origin}${f.url}`;
    navigator.clipboard.writeText(url).then(() => toast.success('URL copiato'));
  };

  return (
    <div className="space-y-4" data-testid="media-library-page">
      <div className="glass-card p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <FolderOpen size={18} className="text-pink-brand" />
            <h2 className="text-white font-bold text-lg">Archivio immagini</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="bg-navy-dark border border-white/20 rounded-lg pl-9 pr-3 py-2 text-white text-sm w-56" />
            </div>
            <select value={folder} onChange={(e) => setFolder(e.target.value)} className="bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
              {folders.map((f) => <option key={f} value={f}>{f === 'all' ? 'Tutte le cartelle' : f}</option>)}
            </select>
            <label className="btn-primary !py-2 !px-4 !text-xs cursor-pointer">
              <Upload size={14} /> Carica file
              <input type="file" multiple accept="image/*,video/*,application/pdf" className="hidden" onChange={(e) => e.target.files && handleFiles(e.target.files)} />
            </label>
          </div>
        </div>
        <p className="text-white/50 text-xs mt-3">Tutti i file caricati nel sito. Usa "Scegli dalla libreria" in qualsiasi sezione per riutilizzarli.</p>
        {busy && <div className="mt-3 text-pink-brand text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento in corso…</div>}
      </div>

      <div className="glass-card p-4">
        {!files ? (
          <div className="text-center py-10"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-white/60 text-sm">
            <ImageOff size={28} className="mx-auto mb-2 text-white/30" />
            Nessun file. Trascina file qui sopra o usa "Carica file".
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {filtered.map((f) => (
              <div key={f.rel} className="group relative bg-navy-dark border border-white/10 rounded-lg overflow-hidden">
                <div className="aspect-square bg-navy-dark flex items-center justify-center">
                  {f.kind === 'image' && <img src={f.url} alt={f.name} className="w-full h-full object-cover" />}
                  {f.kind === 'video' && <Film size={32} className="text-pink-brand" />}
                  {f.kind === 'pdf' && <FileText size={32} className="text-pink-brand" />}
                </div>
                <div className="p-2">
                  <div className="text-white text-[11px] font-semibold truncate" title={f.name}>{f.name}</div>
                  <div className="text-white/50 text-[10px]">{f.folder} · {Math.round(f.size / 1024)} KB</div>
                </div>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => copy(f)} className="w-7 h-7 rounded-full bg-pink-brand text-white flex items-center justify-center" title="Copia URL"><Copy size={11} /></button>
                  <button onClick={() => del(f)} className="w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center" title="Elimina"><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
